import { useId, type ReactNode } from "react";
import { ClockIcon, InfoIcon } from "../../components/skills/icons";
import { useLocale } from "../../i18n/LocaleProvider";
import { isValidToolCallLimit, type ToolCallLimitField } from "./toolCallLimit";

/** A reference estimate and the saved tool-call allowance, beside the start action. */
export function ExecutionEstimate({ value, field, estimate, onChange, disabled, about, controls, notice }: {
  value: unknown;
  /** The allowance's bounds, from the start form's own field. */
  field: ToolCallLimitField;
  /** The workflow's reference run time, from its form; nothing is shown without one. */
  estimate: string | null;
  onChange: (value: number | "") => void;
  disabled: boolean;
  about: string;
  controls: ReactNode;
  notice: string | null;
}) {
  const { t } = useLocale();
  const id = useId();
  const valid = isValidToolCallLimit(value, field);
  const info = [notice, about, t("workSettings.estimateInfo")].filter(Boolean);
  return <section className="work-settings-estimate" aria-label={t("workSettings.execution")}>
    <div className="work-settings-execution-row">
      {controls}
      <div className="work-settings-execution-options">
        <label className="work-settings-budget-inline" htmlFor={id}>
          {t("workSettings.toolLimitShort")}
          <input id={id} type="number" min={field.minimum} max={field.maximum} step={1} value={typeof value === "number" ? value : ""}
          aria-label={t("workSettings.toolLimit")}
          disabled={disabled} aria-invalid={!valid} aria-describedby={!valid ? `${id}-error` : undefined}
          onChange={event => onChange(Number.isFinite(event.target.valueAsNumber) ? event.target.valueAsNumber : "")} />
        </label>
        {estimate ? <span className="work-settings-estimate-time" aria-label={`${t("workSettings.estimatedTime")} ${estimate}`}>
          <ClockIcon size={14} />{estimate}
        </span> : null}
        <span className="info-tip work-settings-estimate-info" tabIndex={0} role="note" aria-label={info.join("\n")}>
          <InfoIcon size={14} /><span className="info-tip-bubble" role="tooltip">{info.map(text => <span key={text}>{text}</span>)}</span>
        </span>
      </div>
    </div>
    {!valid && <p id={`${id}-error`} role="alert" className="work-settings-field-error">{t("workSettings.toolLimitInvalid", { min: field.minimum, max: field.maximum })}</p>}
  </section>;
}
